import React from 'react';
import { message, Tooltip } from 'antd';

import { CLIPBOARD, EMAIL, LOCATION, OPEN, PHONE } from '../icons';
import ColorSwatch, { swatchColorOf } from './ColorSwatch';
import { formatValue } from '../items/itemValueDisplay';
import { copyTextToClipboard } from '../../utils/clipboard';
import { mailtoUrl } from '../../utils/elementFormat/email';
import { mapsUrl } from '../../utils/elementFormat/location';
import { telUrl } from '../../utils/elementFormat/phone';
import { normalizeUrl } from '../../utils/elementFormat/url';
import { EXTERNAL_LINK_PROPS } from './ExternalLink';

/**
 * "Values carry their actions" (docs/platform-standards.md §4) — the rule the
 * measurement ⇄ trigger established, generalised to every value that IS a
 * thing you do something with.
 *
 * A phone number is for calling, an email address for writing to, a location
 * for finding on a map, a URL for opening. On every read surface each of them
 * renders as its formatted text followed by one small trigger, sized and
 * coloured exactly like MeasurementConversionsButton and the help "?" so the
 * affordances read as one family. Compact card tiles drop it
 * (`action={false}`), the way they drop the conversions ⇄: there is no room,
 * and the detail is one tap away.
 *
 * The trigger is an `<a href>`, never a button with a click handler. `tel:`
 * and `mailto:` are the operating system's business, and a phone hands them
 * to the dialler and the mail app only when they arrive as real links — a
 * scripted `window.location` is what browsers block and what long-press,
 * copy-link and "open in new tab" have nothing to do with.
 *
 * ONE action is not a link: a color's **Copy** (docs/plans/data-types-second-pass.md
 * §4.1). Nothing hands a hex to another app, so it is a `<button>` that writes
 * the stored "#rrggbb" to the clipboard (utils/clipboard.js) and says so —
 * the only scripted trigger, and still one that never edits.
 *
 * Nothing about the item changes: a click leaves, it never edits. Both the
 * click and the key press are stopped from reaching the surface behind (a
 * card tile and a table row select on click).
 */

const stop = event => event.stopPropagation();

const elementLabelOf = element => element?.label?.singular || element?.name || 'value';

// A stored url becomes a link ONLY when the URL rule accepts it — the vendored
// utils/elementFormat/url.js every API write path runs (ruling D5) — and the
// link is the rule's spelling of it ('example.com' is what people write, and
// an href without a scheme is a path on THIS site, so a bare host gets
// https://). Anything the rule refuses renders as its text, with no link and
// no trigger: the echo rule keeps values stored before the rule existed
// (smb://, file:///, ms-msdt:, ftp://, a link with a space in it), and those
// are exactly the ones D5 refused for safety — smb can leak Windows
// credentials, ms-* was Follina. (Pre-ship security review, finding W1,
// 2026-10-01: this refused only javascript, data and vbscript.) The rule also
// refuses any whitespace or control character, which is what keeps a
// browser from reading "java\tscript:" as javascript:.
export function urlHref(value) {
	const parsed = normalizeUrl(value);
	return parsed.valid ? parsed.value : '';
}

/**
 * The one action a value of this element carries, or null when it carries
 * none (an empty field, a malformed address, a type with nothing to do).
 *
 *   { href, label, external, Icon }   — or, for a color,
 *   { copy, label, Icon }             — the text Copy writes
 *
 * `external` is what decides `target="_blank"`: a map and a link open a page,
 * so they open a new one and never navigate the app away from an item the
 * reader was in the middle of; `tel:` and `mailto:` hand off to another app
 * entirely and must stay plain, or the browser is left holding an empty tab.
 */
export function valueActionFor(element, value) {
	switch (element?.dataType) {
		case 'phone': {
			const href = telUrl(value);
			return href ? { href, label: 'Call', external: false, Icon: PHONE } : null;
		}
		case 'email': {
			// mailtoUrl answers '' for anything that is not an address, so a
			// legacy or half-typed value grows no trigger that opens a draft
			// to nobody.
			const href = mailtoUrl(value);
			return href ? { href, label: 'Compose email', external: false, Icon: EMAIL } : null;
		}
		case 'location': {
			const href = mapsUrl(value);
			return href ? { href, label: 'Open in Maps', external: true, Icon: LOCATION } : null;
		}
		case 'url': {
			const href = urlHref(value);
			return href ? { href, label: 'Open link', external: true, Icon: OPEN } : null;
		}
		case 'color': {
			// Only a value the color rule reads: a legacy "navy" has no hex
			// to copy, as a malformed address has no draft to open.
			const copy = swatchColorOf(value);
			return copy ? { copy, label: 'Copy', Icon: CLIPBOARD } : null;
		}
		default:
			return null;
	}
}

/** True when this element's values carry an action at all (any value). */
export const hasValueAction = dataType => (
	dataType === 'phone' || dataType === 'email' || dataType === 'location' || dataType === 'url' || dataType === 'color'
);

/**
 * The trigger alone. Renders nothing when there is nothing to act on, so an
 * empty field never grows an affordance.
 */
export function ValueActionButton({ element, value }) {
	const action = valueActionFor(element, value);
	if (!action) return null;
	const { href, label, external, Icon, copy } = action;
	if (copy) {
		const handleCopy = async event => {
			event.stopPropagation();
			if (await copyTextToClipboard(copy)) message.success(`${copy} copied`);
			else message.error('Could not copy the color');
		};
		return (
			<span className='value-action' onClick={stop} onKeyDown={stop} role='presentation'>
				<Tooltip title={label}>
					<button type='button' className='value-action__trigger' aria-label={`${label}: ${elementLabelOf(element)}`} onClick={handleCopy}>
						<Icon />
					</button>
				</Tooltip>
			</span>
		);
	}
	return (
		<span className='value-action' onClick={stop} onKeyDown={stop} role='presentation'>
			<Tooltip title={label}>
				<a
					className='value-action__trigger'
					href={href}
					aria-label={`${label}: ${elementLabelOf(element)}`}
					{...(external ? EXTERNAL_LINK_PROPS : {})}
				>
					<Icon />
				</a>
			</Tooltip>
		</span>
	);
}

/**
 * A value as every read surface shows it: the element's formatted text
 * followed by its action trigger; the same em-dash every other empty value
 * gets when there is nothing to show. `action={false}` drops the trigger
 * where there is no room for it (the compact card tile).
 *
 * The text comes from the same formatValue the rest of the platform prints
 * with, so a phone reads as it dials and a location as its address here,
 * in the table, in a title and in an export.
 */
export default function ValueWithAction({ element, value, action = true, className = '' }) {
	const text = formatValue(element, value);
	if (!text) return '—';
	// A color reads as its swatch and its hex — on the compact tile too,
	// which drops only the trigger.
	const swatch = element?.dataType === 'color' ? <ColorSwatch value={value} /> : null;
	return (
		<span className={`value-with-action ${className}`.trim()}>
			<span className='value-with-action__text'>{swatch}{text}</span>
			{action ? <ValueActionButton element={element} value={value} /> : null}
		</span>
	);
}
